import React from 'react'
import Router, { useRouter } from 'next/router'
import cn from 'classnames'
import { useQueryClient } from 'react-query'

import { Button } from '@components/Buttons'
import { SocialSharing } from '@components/SocialSharing'
import { BASE_URL } from '@configs/config'
import { useTheme } from '@hooks/contexts/ThemeContext/ThemeContext'
import { ThemeEnum } from '@hooks/contexts/ThemeContext/interface'
import { useAuthentication } from '@hooks/useAuthentication'
import { useUpdateBookShelf } from '@hooks/myReading/useUpdateBookShelf'
import { useAlert } from '@hooks/useAlert'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { UserMissionEnum } from '@interfaces/UserMissionEnum'
import { BookMenuIcon } from '@components/icons/BookMenuIcon'
import { CheckIcon } from '@components/icons/CheckIcon'
import { PlusIcon } from '@components/icons/PlusIcon'
import { useDebounce } from '@hooks/useDebounce'
import { BookShelfStatusType } from '@models/book/BookShelfStatusType'
import { EBookIcon } from '@components/icons/EBookIcon'
import { NavigationReadingPageProps } from './interface'
import { ThemeButton } from './components/ThemeButton'
import { SelectChapterForWriter } from './components/SelectChapterForWriter'
import { SelectChapterForReader } from './components/SelectChapterForReader'
import { BookmarkButton } from './components/BookmarkButton'
import { SelectChapterForEbook } from './components/SelectChapterForEbook'

export function NavigationReadingPage({
  mode = UserMissionEnum.WRITER,
  bookId,
  chapterId,
  ebookId,
  chapterName,
  chapterTitle,
  isBookOnShelf,
  isBookmarked,
}: NavigationReadingPageProps) {
  const { debounce } = useDebounce()
  const { isAuthenticated } = useAuthentication()
  const alert = useAlert()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { theme, isDarkTheme, isLightTheme, isSepiaTheme, isNightTheme } =
    useTheme()
  const loginModal = useModal({
    modal: 'login',
  })
  const url =
    mode === UserMissionEnum.WRITER
      ? `/my-writing/${bookId}`
      : mode === UserMissionEnum.READER
      ? `/story/${bookId}`
      : mode === UserMissionEnum.EBOOK_READER
      ? `/ebook/${bookId}`
      : `/my-writing/${bookId}/ebook/${ebookId}`

  const { mutate: updateBookShelf, isLoading: isUpdateBookShefLoading } =
    useUpdateBookShelf(
      async (_, variables) => {
        if (variables.action === 'ADD') alert.success('เพิ่มเข้าชั้นสำเร็จ')
        else alert.success('ลบหนังสือออกจากชั้นสำเร็จ')

        queryClient.setQueryData<BookShelfStatusType | undefined>(
          ['status-shelf', bookId],
          oldData => {
            if (oldData) {
              return {
                isBookOnShelf: variables.action === 'ADD',
              }
            }

            return oldData
          }
        )

        await queryClient.refetchQueries('status-shelf', { active: true })
      },
      () => {
        alert.error('เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาลองอีกครั้ง')
      }
    )

  function handleUpdateBookShelf(action: string) {
    if (isAuthenticated) {
      updateBookShelf({ bookId, action })
    } else {
      loginModal.show()
    }
  }

  return (
    <div
      className={cn(
        'flex justify-between items-center bg-white dark:bg-dark text-secondary dark:text-white px-[30px] w-full h-[80px] text-[14px] sticky top-[60px] z-20 shadow-dropdown mobile:h-[60px] mobile:px-[10px]',
        {
          'bg-sepia': theme === ThemeEnum.SEPIA,
          '!bg-dark-5':
            theme === ThemeEnum.DARK &&
            Router.pathname === '/story/[id]/chapter/[chapterId]',
        }
      )}
    >
      <div className='flex items-center space-x-[20px] mobile:space-x-[10px]'>
        <button
          className={cn(
            'flex font-mitr items-center rounded-[40px] w-[58px] h-[40px] justify-center disabled:opacity-30 bg-primary text-white mobile:w-[40px]',
            {
              'bg-dark-4':
                theme === ThemeEnum.DARK || theme === ThemeEnum.NIGHT,
            }
          )}
          type='button'
          onClick={() => {
            router.push(url)
          }}
        >
          {mode === UserMissionEnum.EBOOK_READER ||
          mode === UserMissionEnum.EBOOK_WRITER ? (
            <EBookIcon
              className={cn({ 'text-primary': theme === ThemeEnum.DARK })}
              width='24'
              height='24'
            />
          ) : (
            <BookMenuIcon
              className={cn({ 'text-primary': theme === ThemeEnum.DARK })}
              width='24'
              height='24'
            />
          )}
        </button>

        {mode === UserMissionEnum.WRITER && (
          <SelectChapterForWriter bookId={bookId} />
        )}
        {mode === UserMissionEnum.READER && (
          <SelectChapterForReader bookId={bookId} />
        )}
        {mode === UserMissionEnum.EBOOK_READER && (
          <SelectChapterForEbook bookId={bookId} />
        )}
        {mode === UserMissionEnum.EBOOK_WRITER && (
          <SelectChapterForEbook bookId={bookId} ebookId={ebookId} />
        )}

        <div
          className={cn('font-light mobile:hidden truncate max-w-[300px]', {
            '!text-gray-16':
              theme === ThemeEnum.DARK &&
              Router.pathname === '/story/[id]/chapter/[chapterId]',
          })}
        >
          {chapterTitle} {chapterName}
        </div>
      </div>
      <div className='flex items-center space-x-[20px] mobile:space-x-[10px]'>
        <ThemeButton />
        {mode === UserMissionEnum.READER && (
          <>
            <BookmarkButton
              bookId={bookId}
              chapterId={chapterId}
              isLightTheme={isLightTheme}
              isNightTheme={isNightTheme}
              isBookmarked={!!isBookmarked}
              isSepiaTheme={isSepiaTheme}
              isDarkTheme={isDarkTheme}
              isAuthenticated={isAuthenticated}
            />
            <Button
              type='button'
              className={cn(
                'tablet-mobile:w-[30px] tablet-mobile:h-[30px] tablet-mobile:rounded-full tablet-mobile:p-0 font-medium text-[14px] border dark:bg-dark-9 dark:text-dark-10 dark:border-dark-3',
                {
                  'border-gray-4 bg-gray-4 text-secondary':
                    isLightTheme && !isBookOnShelf,
                  'border-white bg-white text-secondary':
                    isSepiaTheme && !isBookOnShelf,
                  'border-primary bg-white text-primary':
                    (isLightTheme || isSepiaTheme) && isBookOnShelf,
                  '!border-dark-3 !bg-dark-3 !text-gray-16':
                    isDarkTheme && !isBookOnShelf,
                  '!border-dark-3 !bg-dark-3 !text-dark-10':
                    isNightTheme && !isBookOnShelf,
                  '!border-dark-4 !bg-dark-4 !text-primary':
                    (isDarkTheme || isNightTheme) && isBookOnShelf,
                }
              )}
              onClick={() => {
                debounce(() => {
                  if (isBookOnShelf) handleUpdateBookShelf('REMOVE')
                  else handleUpdateBookShelf('ADD')
                }, 400)
              }}
              disabled={isUpdateBookShefLoading}
            >
              <div className='flex items-center space-x-[5px]'>
                {isBookOnShelf ? <CheckIcon /> : <PlusIcon />}
                <span className='tablet-mobile:hidden'>เพิ่มเข้าชั้น</span>
              </div>
            </Button>
            <SocialSharing
              shareUrl={`${BASE_URL}/story/${bookId}/chapter/${chapterId}`}
              type='chapter'
              buttonText='แชร์'
              bookId={bookId}
              bookChapterId={chapterId}
            />
          </>
        )}
      </div>
    </div>
  )
}
